/**
 * Coordination context between the patient-edit shell and its tab bodies.
 *
 * The shell owns the top-bar Save / Next button and the Cancel affordance.
 * Each tab body owns the actual form (RHF + Zod) and the dirty-state /
 * save-enabled logic. This context is the registration point: a tab body
 * calls `setSaveAction({ label, onClick, disabled, busy })` and
 * `setDirty(isDirty)` in a `useEffect`, and the shell reads those values
 * to render the buttons + intercept Cancel with a confirm dialog when
 * appropriate.
 *
 * Why a context instead of prop-drilling: tab bodies are mounted via a
 * routes/tabs lookup, not as direct children. Props don't reach them
 * cleanly without threading through `PatientEditPage → Tabs → TabsContent
 * → tab component`. The context skips that.
 */

import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';

export interface SaveAction {
  /** Button label — "Save" in edit-mode, "Next" on tab 0 of create-mode. */
  label: string;
  onClick: () => void;
  disabled?: boolean;
  /** Appointment page: the Save appears only while the form has unsaved changes (false = not rendered). */
  dirty?: boolean;
  /** A save succeeded in this visit and nothing is left to save: the shell shows "Changes saved" instead of Save. */
  saved?: boolean;
  /** When true, the button shows "<label>…" and is non-interactive. */
  busy?: boolean;
  /**
   * Human-readable reason the button is disabled (e.g. "Email is required.").
   * The shell surfaces this next to the disabled Save button so the user
   * isn't left guessing which required field is still blocking the save.
   * Null when the button is enabled (or disabled only because it's busy).
   */
  disabledReason?: string | null;
  /**
   * Why the last save did not go through (the same words the failure toast carries). The shell shows it as the form
   * action bar's error status until the next save attempt. Null / absent when the last attempt did not fail.
   */
  errorText?: string | null;
}

interface PatientEditShellContextValue {
  saveAction: SaveAction | null;
  setSaveAction: (action: SaveAction | null) => void;
  /** True when the active tab's form has unsaved edits. Shell uses this
   *  to prompt-confirm on Cancel. */
  isDirty: boolean;
  setIsDirty: (dirty: boolean) => void;
}

const PatientEditShellContext = createContext<PatientEditShellContextValue | null>(null);

interface PatientEditShellProviderProps {
  children: ReactNode;
}

export function PatientEditShellProvider({ children }: PatientEditShellProviderProps) {
  const [saveAction, setSaveAction] = useState<SaveAction | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const value = useMemo(
    () => ({ saveAction, setSaveAction, isDirty, setIsDirty }),
    [saveAction, isDirty],
  );
  return (
    <PatientEditShellContext.Provider value={value}>
      {children}
    </PatientEditShellContext.Provider>
  );
}

/**
 * Read the shell context. Calling outside a `<PatientEditShellProvider>`
 * returns a no-op shape so tab bodies can render in Storybook without
 * mounting the full shell.
 */
export function usePatientEditShell(): PatientEditShellContextValue {
  const ctx = useContext(PatientEditShellContext);
  if (!ctx) {
    return {
      saveAction: null,
      setSaveAction: () => {},
      isDirty: false,
      setIsDirty: () => {},
    };
  }
  return ctx;
}
